import React, { useEffect, useRef, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { bankTransfersAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";
import BankTransfersTable from "./BankTransfersTable";
import BankTransfersCategoryFilter from "./BankTransfersCategoryFilter";
import "../../../css/Views/BankTransfersView.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft, faArrowRight, faRotate, faChevronDown } from "@fortawesome/free-solid-svg-icons";

const TRIP_RELATED_CATEGORY_IDS = [1, 2, 3, 5];
const MEMBERSHIP_FEE_CATEGORY_ID = 9;

const BankTransfersView = () => {
    const { callApi, isLoading, error } = useApi();
    const { callApi: callCategoryApi } = useApi();
    const { callApi: callRelatedTripApi } = useApi();
    const { callApi: callMembershipMemberApi } = useApi();
    const { callApi: callTripsApi, isLoading: isLoadingTrips } = useApi();
    const { callApi: callSynchronizeApi, isLoading: isSynchronizing } = useApi();

    const [withoutRelatedTrip, setWithoutRelatedTrip] = useState(false);
    const [withoutMembershipMember, setWithoutMembershipMember] = useState(false);
    const [transfers, setTransfers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [transferType, setTransferType] = useState("all");
    const [categoryId, setCategoryId] = useState(null);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [groupByCategory, setGroupByCategory] = useState(false);
    const [showOtherFilters, setShowOtherFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [hasLoadedTransfers, setHasLoadedTransfers] = useState(false);
    const [updatingTransferGuid, setUpdatingTransferGuid] = useState(null);
    const [relatedTripOptions, setRelatedTripOptions] = useState([]);
    const [membershipMemberOptions, setMembershipMemberOptions] = useState([]);
    const [lastSynchronization, setLastSynchronization] = useState(null);

    const disablePagination = Boolean(dateFrom || dateTo);
    const otherFiltersRef = useRef(null);

    const fetchTransfers = () => {
        callApi(
            () => bankTransfersAPI.getTransfers({
                search: searchTerm,
                page: disablePagination ? 1 : page,
                pageSize: disablePagination ? null : pageSize,
                transferType,
                categoryId,
                dateFrom: dateFrom || null,
                dateTo: dateTo || null,
                withoutRelatedTrip,
                withoutMembershipMember,
                disablePagination
            }),
            (data) => {
                setTransfers(data.items);
                setTotalCount(data.totalCount);
                setLastSynchronization(data.lastSynchronization ?? null);
                setHasLoadedTransfers(true);
            }
        );
    };

    const fetchCategories = () => {
        callApi(
            () => bankTransfersAPI.getCategories(),
            (data) => setCategories(data)
        );
    };

    const fetchRelatedTripOptions = () => {
        callTripsApi(
            () => bankTransfersAPI.getRelatedTripOptions(),
            (data) => setRelatedTripOptions(Array.isArray(data) ? data : [])
        );
    };

    const fetchMembershipMemberOptions = () => {
        callMembershipMemberApi(
            () => bankTransfersAPI.getMembershipMemberOptions(),
            (data) => setMembershipMemberOptions(Array.isArray(data) ? data : [])
        );
    };

    useEffect(() => {
        fetchTransfers();
    }, [page, searchTrigger, categoryId]);

    useEffect(() => {
        fetchCategories();
        fetchMembershipMemberOptions();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (otherFiltersRef.current && !otherFiltersRef.current.contains(event.target))
                setShowOtherFilters(false);
        };

        document.addEventListener("pointerdown", handleClickOutside);

        return () => {
            document.removeEventListener("pointerdown", handleClickOutside);
        };
    }, []);

    const maxPage = Math.ceil(totalCount / pageSize);

    const handleSearchClick = () => {
        setPage(1);
        setHasLoadedTransfers(false);
        setSearchTrigger((value) => value + 1);
    };

    const handleCategoryFilterChange = (newCategoryId) => {
        if (newCategoryId === categoryId)
            return;

        setPage(1);
        setHasLoadedTransfers(false);
        setCategoryId(newCategoryId);
    };

    const handleCategoryChange = (guid, newCategoryId) => {
        setUpdatingTransferGuid(guid);

        callCategoryApi(
            () => bankTransfersAPI.changeCategory(guid, newCategoryId),
            () => {
                const category = categories.find((item) => item.id === newCategoryId);
                const canHaveRelatedTrip = TRIP_RELATED_CATEGORY_IDS.includes(newCategoryId);
                const canHaveMembershipMember = newCategoryId === MEMBERSHIP_FEE_CATEGORY_ID;

                setTransfers((current) =>
                    current.map((transfer) =>
                        transfer.guid === guid
                            ? {
                                ...transfer,
                                categoryId: newCategoryId,
                                categoryName: category?.name ?? null,
                                groupId: category?.groupId ?? null,
                                groupName: category?.groupName ?? null,
                                relatedTripId: canHaveRelatedTrip ? transfer.relatedTripId : null,
                                relatedTripName: canHaveRelatedTrip ? transfer.relatedTripName : null,
                                membershipMemberId: canHaveMembershipMember ? transfer.membershipMemberId : null,
                                membershipMemberName: canHaveMembershipMember ? transfer.membershipMemberName : null
                            }
                            : transfer
                    )
                );

                setUpdatingTransferGuid(null);
            },
            () => {
                setUpdatingTransferGuid(null);
            }
        );
    };

    const handleRelatedTripChange = (guid, relatedTripId) => {
        setUpdatingTransferGuid(guid);

        callRelatedTripApi(
            () => bankTransfersAPI.changeRelatedTrip(guid, relatedTripId),
            () => {
                const trip = relatedTripId != null
                    ? relatedTripOptions.find((item) => item.id === relatedTripId)
                    : null;

                setTransfers((current) =>
                    current.map((transfer) =>
                        transfer.guid === guid
                            ? {
                                ...transfer,
                                relatedTripId,
                                relatedTripName: trip?.name ?? null
                            }
                            : transfer
                    )
                );

                setUpdatingTransferGuid(null);
            },
            () => {
                setUpdatingTransferGuid(null);
            }
        );
    };

    const handleMembershipMemberChange = (guid, membershipMemberId) => {
        setUpdatingTransferGuid(guid);

        callMembershipMemberApi(
            () => bankTransfersAPI.changeMembershipMember(guid, membershipMemberId),
            () => {
                const member = membershipMemberId != null
                    ? membershipMemberOptions.find((item) => item.id === membershipMemberId)
                    : null;

                setTransfers((current) =>
                    current.map((transfer) =>
                        transfer.guid === guid
                            ? {
                                ...transfer,
                                membershipMemberId,
                                membershipMemberName: member?.name ?? null
                            }
                            : transfer
                    )
                );

                setUpdatingTransferGuid(null);
            },
            () => {
                setUpdatingTransferGuid(null);
            }
        );
    };

    const handleRelatedTripMenuOpen = () => {
        if (relatedTripOptions.length === 0 && !isLoadingTrips)
            fetchRelatedTripOptions();
    };

    const handleSynchronize = () => {
        callSynchronizeApi(
            () => bankTransfersAPI.synchronize(),
            () => fetchTransfers()
        );
    };

const handleWithoutRelatedTripChange = (checked) => {
    setWithoutRelatedTrip(checked);

    if (checked)
        setWithoutMembershipMember(false);

    setPage(1);
    setHasLoadedTransfers(false);
    setSearchTrigger((value) => value + 1);
};

const handleWithoutMembershipMemberChange = (checked) => {
    setWithoutMembershipMember(checked);

    if (checked)
        setWithoutRelatedTrip(false);

    setPage(1);
    setHasLoadedTransfers(false);
    setSearchTrigger((value) => value + 1);
};

    return (
        <div className="bank-transfers-view">
            <div className="bank-transfer-filters">
                <div className="bank-transfer-filter-main">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Szukaj po tytule, osobie lub kwocie..."
                        className="bank-transfers-filter-search"
                        onKeyDown={(e) => {
                            if (e.key === "Enter")
                                handleSearchClick();
                        }}
                    />

                    <select
                        value={transferType}
                        onChange={(e) => setTransferType(e.target.value)}
                        className="bank-transfers-filter-select"
                    >
                        <option value="all">Wszystkie operacje</option>
                        <option value="payment">Wpływy</option>
                        <option value="refund">Wydatki</option>
                    </select>
                </div>

                <div className="bank-transfer-filter-secondary">
                    <div className="bank-transfer-date-range">
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="bank-transfers-filter-date"
                        />

                        <span className="bank-transfer-date-separator">–</span>

                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="bank-transfers-filter-date"
                        />
                    </div>

                    <BankTransfersCategoryFilter
                        value={categoryId}
                        categories={categories}
                        onChange={handleCategoryFilterChange}
                    />

                    <div className="bank-transfers-more-filter" ref={otherFiltersRef}>
                        <button
                            type="button"
                            className={`bank-transfers-more-button ${withoutRelatedTrip || withoutMembershipMember || groupByCategory ? "active" : ""}`}
                            onClick={() => setShowOtherFilters((value) => !value)}
                        >
                            <span>Inne</span>

                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`bank-transfers-more-chevron ${showOtherFilters ? "open" : ""}`}
                            />

                            {(withoutRelatedTrip || withoutMembershipMember || groupByCategory) && (
                                <span className="bank-transfers-more-indicator" />
                            )}
                        </button>

                        {showOtherFilters && (
                            <div className="bank-transfers-more-menu">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={withoutRelatedTrip}
                                        onChange={(e) => handleWithoutRelatedTripChange(e.target.checked)}
                                    />
                                    <span>Bez przypisanej wycieczki</span>
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        checked={withoutMembershipMember}
                                        onChange={(e) => handleWithoutMembershipMemberChange(e.target.checked)}
                                    />
                                    <span>Składki bez przypisanej osoby</span>
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        checked={groupByCategory}
                                        onChange={(e) => setGroupByCategory(e.target.checked)}
                                    />
                                    <span>Grupuj według kategorii</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={handleSearchClick}
                        className="bank-transfers-filter-search-action"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                        Szukaj
                    </button>

                    <div className="bank-transfers-sync-inline">
                        <button
                            type="button"
                            className="bank-transfers-sync-button"
                            onClick={handleSynchronize}
                            disabled={isSynchronizing}
                            title="Synchronizuj przelewy"
                        >
                            <FontAwesomeIcon icon={faRotate} spin={isSynchronizing} />
                            <span>{isSynchronizing ? "Synchronizacja..." : "Synchronizuj"}</span>
                        </button>

                        <span className="bank-transfers-sync-info">
                            {lastSynchronization
                                ? new Date(lastSynchronization).toLocaleString("pl-PL")
                                : "Brak synchronizacji"}
                        </span>
                    </div>
                </div>
            </div>

            {(!hasLoadedTransfers || isLoading) && (
                <Loading text="Ładowanie przelewów..." />
            )}

            {error && (
                <p className="error">
                    Błąd: {error.message}
                </p>
            )}

            {!isLoading && hasLoadedTransfers && transfers.length === 0 && (
                <p>Brak przelewów do wyświetlenia.</p>
            )}

            {!isLoading && hasLoadedTransfers && transfers.length > 0 && (
                <>
                    <BankTransfersTable
                        transfers={transfers}
                        categories={categories}
                        relatedTripOptions={relatedTripOptions}
                        membershipMemberOptions={membershipMemberOptions}
                        onCategoryChange={handleCategoryChange}
                        onRelatedTripChange={handleRelatedTripChange}
                        onMembershipMemberChange={handleMembershipMemberChange}
                        onRelatedTripMenuOpen={handleRelatedTripMenuOpen}
                        groupByCategory={groupByCategory}
                        updatingTransferGuid={updatingTransferGuid}
                        isLoadingTrips={isLoadingTrips}
                    />

                    {!disablePagination && (
                        <div className="pagination-controls">
                            {page > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setPage((current) => current - 1)}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    Poprzednia
                                </button>
                            )}

                            <span>Strona {page} z {maxPage}</span>

                            {page < maxPage && (
                                <button
                                    type="button"
                                    onClick={() => setPage((current) => current + 1)}
                                >
                                    Następna
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BankTransfersView;